import jQuery from 'jquery';
import {
    diffusionServiceUtils,
    pricesStateStore,
    pricesProvider,
    PRICE_HANDLER_EVENTS
} from 'rp-prices-component';
import { shouldEstablishSession } from 'rp-diffusion-connector';

import HorsesRowStore from './HorsesRowsStore';
import AnimationHandler from './AnimationHandler';
import OddsTabHeader from './OddsTabHeader';
import DiffusionProvider from './DiffusionProvider';
import OddsTabSort from './OddsTabSort';
import { batchedExecute } from '../../helpers/utils';

function getOrderingValue(runnerPriceInfo) {
    if (!runnerPriceInfo) {
        return 9999; // no price
    }
    if ([runnerPriceInfo.status, pricesStateStore.getRaceInfo(runnerPriceInfo)].indexOf('H') !== -1) {
        return 9998; // '-'
    }
    if (runnerPriceInfo.fractionalPrice === 'SP') {
        return 9997; // 'SP'
    }
    const decimal = runnerPriceInfo.decimalPrice;
    if (decimal === undefined || decimal === '' || decimal === null || isNaN(decimal)) {
        return 9999; // no price
    }
    return parseFloat(runnerPriceInfo.decimalPrice);
}

export default class OddsTabHandler {
    constructor() {
        this.onUserSettingsChanged = this.onUserSettingsChanged.bind(this);
        this.onBestOddsData = this.onBestOddsData.bind(this);
        this.handleRaceInfo = this.handleRaceInfo.bind(this);
    }

    init() {
        const pageConfig = this.getPageConfig();
        this.horsesRowStore = new HorsesRowStore(pageConfig);
        this.horsesRowStore.init();
        this.oddsTabSort = new OddsTabSort(this.horsesRowStore, getOrderingValue);
        this.animationHandler = new AnimationHandler();
        this.oddsTabHeader = new OddsTabHeader({
            onBookMakerChanged: this.oddsTabSort.sortByBookiePrices
        });
        const horsesNames = this.horsesRowStore
            .getHorses()
            .map(({ horseName }) => horseName);
        const limit = this.getThrottleLimit(horsesNames.length);
        this.renderPrice = batchedExecute(this.renderPrices, limit, this);
        this.diffusionProvider = shouldEstablishSession() ? new DiffusionProvider(
            pageConfig,
            horsesNames,
            {
                onInfo: this.handleRaceInfo,
                onBestOdds: this.onBestOddsData,
                onPrice: this.renderPrice
            }
        ) : null;

        this.sortByTopPrices =
            batchedExecute(this.oddsTabSort.sortByTopPrices, limit, this.oddsTabSort);
        this.animationHandler.init();
        this.oddsTabHeader.init();
        this.oddsTabSort.sortByTopPrices(); // just to sort by horse name before any price arrives
        this.bindEvents();
        const customizeSettingsCookie = jQuery.cookie('customizeSettings');
        if (customizeSettingsCookie) {
            const customizeSettings = JSON.parse(customizeSettingsCookie);
            pricesStateStore.setPriceViewType({ showDecimal: customizeSettings.decimalOdds });
        }
        if (this.diffusionProvider) {
            this.diffusionProvider.init();
        }
    }

    /**
     * @private
     */
    getPageConfig() {
        const pageConfigElement = jQuery('.js-diffusionTopicSettingsRace')[0];
        return {
            raceTime: diffusionServiceUtils.getTopicRaceTime(pageConfigElement),
            raceDate: diffusionServiceUtils.getTopicRaceDate(pageConfigElement),
            courseName: diffusionServiceUtils.getTopicCourseName(pageConfigElement)
        };
    }

    /**
     * @private
     */
    getThrottleLimit(horsesCount) {
        const limit = (horsesCount * horsesCount) / 4;
        if (limit > 1000) {
            return 1000;
        }
        if (limit < 25) {
            return 25;
        }
        return limit;
    }

    /**
     * @private
     */
    bindEvents() {
        // subscribing on event because price component redraws only bet buttons
        pricesProvider.subscribe(
            PRICE_HANDLER_EVENTS.PRICES_SETTINGS_CHANGED,
            this.onUserSettingsChanged
        );

        this.addBetslipEventsListeners();
    }

    /**
     * @private
     */
    onUserSettingsChanged() {
        // rerender prices
        this.horsesRowStore.getPrices().forEach((price) => {
            // eslint-disable-next-line no-param-reassign
            price.priceElement.innerHTML = diffusionServiceUtils.getDisplayedPriceValue(price);
        });
    }

    /**
     * @private
     */
    renderPrices(renderConfigs) {
        renderConfigs.forEach((renderConfig) => {
            this.renderSinglePrice(...renderConfig);
        });
        const selectedBookie = this.oddsTabHeader.getSelectedBookie();
        if (renderConfigs.some(([{ bookmaker }]) => bookmaker === selectedBookie)) {
            this.oddsTabSort.sortByBookiePrices(selectedBookie);
        }
        const horsesToUpdate = renderConfigs
            .map(([{ horseName }]) => horseName)
            .filter((value, index, self) => self.indexOf(value) === index);
        this.accentTopPrices(horsesToUpdate);
        this.setAddedToBetslipClass();
    }

    /**
     * @private
     */
    renderSinglePrice(renderConfig) {
        const {
            horseName,
            bookmaker,
            outcomersId,
            fractionalPrice,
            decimalPrice,
            historyPrice,
            eventId,
            marketId,
            meetingId,
            status
        } = renderConfig;

        const priceSelector = {
            horseName,
            bookmaker,
        };
        const [priceConfig] = this.horsesRowStore.getPrices(priceSelector);
        const {
            priceElement,
            orderingValue: historyOrdering
        } = priceConfig;
        const newPrice = diffusionServiceUtils.getDisplayedPriceValue(renderConfig);
        const currentPrice = priceElement.innerHTML;
        const newStatus = pricesStateStore.getRaceInfo(renderConfig) === 'H'
            ? 'H'
            : status;
        const orderingValue = getOrderingValue(renderConfig);
        const price = {
            outcomersId,
            fractionalPrice,
            decimalPrice,
            historyPrice,
            eventId,
            meetingId,
            status: newStatus,
            orderingValue,
            newPrice
        };

        this.horsesRowStore.updatePrice(priceSelector, price);

        if (bookmaker === 'TOTE') {
            if (parseFloat(decimalPrice) >= 100 || parseFloat(fractionalPrice) >= 100) {
                jQuery(priceElement).addClass('RC-oddsRunnerContent__price_large');
            } else {
                jQuery(priceElement).removeClass('RC-oddsRunnerContent__price_large');
            }
        }

        jQuery(priceElement)
            .attr({
                'data-diffusion-outcomers-id': outcomersId,
                'data-diffusion-fractional': fractionalPrice,
                'data-diffusion-decimal': decimalPrice,
                'data-diffusion-history': historyPrice,
                'data-diffusion-status': newStatus, // for correct calculation ordering value
                'data-diffusion-event-id': eventId,
                'data-diffusion-market-id': marketId,
                'data-diffusion-meeting-id': meetingId,
                'data-diffusion-next-price': newPrice,
            })
            .data({
                HistoryPrice: historyPrice,
                OrderingValue: orderingValue,
                NewPrice: newPrice
            });
        if (orderingValue === historyOrdering) {
            return;
        }
        if (orderingValue > 9000 || historyOrdering > 9000) {
            const pointerEnabled = !(orderingValue && orderingValue >= 9998);
            this.animationHandler.setPointerEnabled(priceElement, pointerEnabled);
            priceElement.innerHTML = newPrice;
            this.animationHandler.clearPriceEffects(priceElement);
        } else {
            let isTransitionRequired = true;
            if (!/\S/.test(currentPrice)) {
                priceElement.innerHTML = newPrice;
                isTransitionRequired = false;
            }
            this.animationHandler.markPriceDelta(priceConfig, isTransitionRequired);
        }
    }

    /**
     * @private
     */
    accentTopPrices(horsesToUpdate) {
        const boldClass = 'RC-oddsRunnerContent__price_bold';
        const getPriceOrder =
            price => (
                (
                    price.fractionalPrice === 'SP' ||
                    price.status === 'H' ||
                    price.decimalPrice === '' ||
                    price.decimalPrice === null ||
                    price.decimalPrice === undefined
                )
                    ? -1 // 'SP' || '-' || no price
                    : Number(price.decimalPrice)
            );
        this.horsesRowStore
            .getHorses(horsesToUpdate)
            .forEach(({ prices }) => {
                const topPrice = prices
                    .reduce((max, price) => (Math.max(max, getPriceOrder(price))), 0);
                if (topPrice) {
                    prices
                        .forEach(({ priceElement: { classList }, decimalPrice }) => {
                            if (Number(decimalPrice) === topPrice) {
                                classList.add(boldClass);
                            } else {
                                classList.remove(boldClass);
                            }
                        });
                }
            });
    }

    /**
     * @private
     */
    setAddedToBetslipClass() {
        let nextSelections;
        if (window.betslipApi) {
            nextSelections = window.betslipApi.getSelectedBets();
            jQuery('.added-to-betslip').removeClass('added-to-betslip');

            nextSelections.forEach((selection) => {
                jQuery(
                    `[data-diffusion-outcomers-id="${
                        selection.outcomersId
                    }"][data-diffusion-meeting-id="${
                        selection.meetingId
                    }"]`
                ).addClass('added-to-betslip');
            });
        }
    }

    /**
     * @private
     */
    addBetslipEventsListeners() {
        jQuery(document).on('BETSLIP.MAKE_SELECTION', () => {
            this.setAddedToBetslipClass();
        });

        jQuery(document).on('BETSLIP.SELECTION_REMOVED', () => {
            this.setAddedToBetslipClass();
        });
        jQuery(document).on('BETSLIP.INITIALIZED', () => {
            this.setAddedToBetslipClass();
        });
    }

    /**
     * @private
     */
    onBestOddsData(parcedTopic) {
        this.horsesRowStore.updateHorse(parcedTopic.horseName, {
            bestOdds: parcedTopic
        });
        const selectedBookie = this.oddsTabHeader.getSelectedBookie();
        if (!selectedBookie) {
            this.sortByTopPrices();
        }
    }

    /**
     * @private
     */
    handleRaceInfo(raceConfig) {
        this.oddsTabHeader.updateHeaderUI(raceConfig);
        pricesStateStore.updateRaceInfo(raceConfig);
        if (raceConfig.suspendState === 'H') {
            this.updatePricesForStatus(raceConfig);
        }
    }

    /**
     * @private
     */
    updatePricesForStatus({ bookmaker }) {
        this.horsesRowStore
            .getPrices({ bookmaker })
            .forEach(this.renderPrice);
    }
}



// WEBPACK FOOTER //
// ./lib/js/modules/oddsTabHandler/OddsTabHandler.js